import { forwardRef, useEffect, useRef, useState } from "react";
import { Autocomplete, Avatar, Chip, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ConnectedBookingData } from "../../../features/Booking-Form/typings/connected-bookingData";
import { ColleagueFilterState } from "./types/GroupFilters.type";
import { calculateTagLimit } from "./functions/calculateTagLimit.function";

type P = {
  connectedBookingData: ConnectedBookingData[] | undefined;
  selectedColleagueFilter: ConnectedBookingData[];
  setSelectedColleagueFilter: (filter: ConnectedBookingData[]) => void;
  onClose?: () => void;
  colleagueFilterState: ColleagueFilterState;
  /** Booking flow selects all colleagues when the filter opens; browse surfaces can opt out. */
  selectAllOnMount?: boolean;
};

function getColleagueName(option: ConnectedBookingData): string {
  return `${option.firstName} ${option.lastName}`;
}

function getColleagueInitials(option: ConnectedBookingData): string {
  return `${option.firstName.trim().charAt(0)}${option.lastName.trim().charAt(0)}`.toUpperCase();
}

function ColleagueAvatar({ option }: Readonly<{ option: ConnectedBookingData }>) {
  return (
    <Avatar
      aria-hidden
      sx={theme => ({
        width: 24,
        height: 24,
        fontSize: theme.typography.caption.fontSize,
        bgcolor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText
      })}
    >
      {getColleagueInitials(option)}
    </Avatar>
  );
}

export const ColleagueBookingFilters: React.FC<P> = forwardRef<HTMLDivElement, P>((props, ref) => {
  const {
    connectedBookingData,
    selectedColleagueFilter,
    setSelectedColleagueFilter,
    colleagueFilterState,
    selectAllOnMount = true
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [tagLimit, setTagLimit] = useState(2);

  const { t } = useTranslation();

  const [options, setOptions] = useState<ConnectedBookingData[]>([]);

  const handleColleaguesChange = (event: React.SyntheticEvent, value: ConnectedBookingData[]) => {
    setSelectedColleagueFilter(value);
  };

  useEffect(() => {
    // initially select all the booking data items
    if (selectAllOnMount) {
      setSelectedColleagueFilter(connectedBookingData ?? []);
    }
  }, []);

  useEffect(() => {
    if (!connectedBookingData) return setOptions([]);
    setOptions(connectedBookingData);
  }, [connectedBookingData]);

  useEffect(() => {
    // when the colleagues booking filter toggled to be empty(second click)
    if (colleagueFilterState === ColleagueFilterState.EMPTY) {
      setSelectedColleagueFilter([]);
    }
  }, [colleagueFilterState]);

  // Observe Autocomplete container width changes
  useEffect(() => {
    if (
      selectedColleagueFilter.length > 0 &&
      colleagueFilterState !== ColleagueFilterState.DEACTIVATED
    ) {
      const resizeObserver = new ResizeObserver(() => {
        // 30 padding + extra 34 padding so the content stays in one line only
        calculateTagLimit(containerRef, 64, selectedColleagueFilter, setTagLimit);
      });

      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      // Initial calculation
      calculateTagLimit(containerRef, 64, selectedColleagueFilter, setTagLimit);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [selectedColleagueFilter, colleagueFilterState]);

  return (
    <div ref={containerRef}>
      <Autocomplete
        ref={ref}
        data-testid="find-colleagues-autocomplete"
        multiple
        disableCloseOnSelect
        id="tags-outlined8"
        options={options}
        disableClearable
        value={selectedColleagueFilter}
        onChange={handleColleaguesChange}
        renderOption={(props, option) => <BookingMemeberOptionList props={props} option={option} />}
        noOptionsText={t("No Options")}
        renderInput={params => (
          <TextField
            {...params}
            fullWidth
            data-testid={"find-colleagues-field"}
            placeholder={t("Find your colleagues")}
          />
        )}
        limitTags={tagLimit}
        renderTags={(tagValue, getTagProps) => {
          return tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.userId}
              data-testid={`${option.userId}-select-chip`}
              avatar={<ColleagueAvatar option={option} />}
              label={getColleagueName(option)}
            />
          ));
        }}
        getOptionLabel={getColleagueName}
        isOptionEqualToValue={(option, value) => option.userId === value.userId}
      />
    </div>
  );
});

export function BookingMemeberOptionList({
  props,
  option
}: Readonly<{
  props: React.HTMLAttributes<HTMLLIElement>;
  option: ConnectedBookingData;
}>) {
  return (
    <li {...props} key={option.userId}>
      <Stack data-testid="team-selection-option" direction="row" spacing={1} alignItems="center">
        <ColleagueAvatar option={option} />
        <span>{getColleagueName(option)}</span>
      </Stack>
    </li>
  );
}
